import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ClipboardList, Loader2, Printer, RefreshCw, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePrintPrivacy } from '@/hooks/usePrintPrivacy';
import WorkModalShell from '@/components/common/WorkModalShell';
import CourierHandoverSheets from '@/components/printing/CourierHandoverSheets';
import {
  groupHandoverRows,
  handoverDuplicates,
  handoverSelectionFromRows,
  HandoverPreparationError,
  loadHandoverSelection,
  type HandoverIssue,
  type HandoverPage,
  type HandoverRow,
  type HandoverSelection,
} from '@/lib/courierHandover';
import { loadPrintSender, type SavedPrintSender } from '@/lib/printSenderSettings';
import {
  assertHandoverFits,
  measureHandoverPages,
  waitForHandoverResources,
} from '@/utils/handoverLayout';
import { customerPrintHtml } from '@/utils/customerPrintPrivacy';
import { printHtmlContent } from '@/utils/printUtils';
import { cn } from '@/lib/utils';
import { deliveryButton } from './DeliveryUpdateDialog';

export default function CourierHandoverDialog({
  selection,
  onClose,
}: {
  selection: HandoverSelection[];
  onClose: () => void;
}) {
  const { organization, can, profile, access } = useAuth();
  const [rows, setRows] = useState<HandoverRow[] | null>(null),
    [issues, setIssues] = useState<HandoverIssue[]>([]);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [loading, setLoading] = useState(false),
    [progress, setProgress] = useState({ done: 0, total: selection.length });
  const [savedSender, setSavedSender] = useState<SavedPrintSender | null>(null),
    [senderError, setSenderError] = useState(''),
    [brandOnly, setBrandOnly] = useState(false);
  const [includeSender, setIncludeSender] = useState(true),
    [includePhone, setIncludePhone] = useState(false),
    [acknowledged, setAcknowledged] = useState(false);
  const [preparedAt, setPreparedAt] = useState(() => new Date().toISOString()),
    [pages, setPages] = useState<HandoverPage[] | null>(null),
    [layoutError, setLayoutError] = useState('');
  const [checking, setChecking] = useState(false),
    [printing, setPrinting] = useState(false),
    [actualSize, setActualSize] = useState(false),
    [scale, setScale] = useState(1),
    [reload, setReload] = useState(0);
  const operation = useRef<AbortController | null>(null),
    alive = useRef(true),
    measuring = useRef<HTMLDivElement>(null),
    preview = useRef<HTMLDivElement>(null),
    viewport = useRef<HTMLDivElement>(null),
    printButton = useRef<HTMLButtonElement>(null);
  const { askPrivacy, privacyDialog } = usePrintPrivacy(can('sales.view'));
  const timezone = useMemo(() => {
    const configured = organization?.timezone || 'Asia/Kolkata';
    try {
      new Intl.DateTimeFormat('en-IN', { timeZone: configured }).format();
      return configured;
    } catch {
      return 'UTC';
    }
  }, [organization?.timezone]);
  const groups = useMemo(() => groupHandoverRows(rows || []), [rows]);
  const duplicates = useMemo(() => handoverDuplicates(groups), [groups]);
  const sender = includeSender ? savedSender?.sender || null : null;
  const busy = loading || checking || printing;
  const senderReady = !senderError || brandOnly;
  const ready =
    !!rows?.length &&
    !!pages?.length &&
    !layoutError &&
    senderReady &&
    (!includeSender || !!savedSender) &&
    (!duplicates.length || acknowledged) &&
    can('sales.view');
  const measurementPages = useMemo(
    () =>
      groups.map((group) => ({
        courier: group.courier,
        rows: group.rows,
        groupRecords: group.rows.length,
        groupPage: 1,
        groupPages: 1,
        start: 0,
      })),
    [groups],
  );

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      operation.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (!organization?.id || !can('sales.view')) return;
    const request = new AbortController();
    operation.current = request;
    setLoading(true);
    setRows(null);
    setPages(null);
    setIssues([]);
    setError('');
    setNotice('');
    setSenderError('');
    setAcknowledged(false);
    setBrandOnly(false);
    const timeout = setTimeout(() => {
      request.abort();
      if (operation.current !== request || !alive.current) return;
      operation.current = null;
      setLoading(false);
      setError('The saved details took too long to load. Check your connection and prepare again.');
    }, 15000);
    void Promise.allSettled([
      loadHandoverSelection(selection, request.signal, (done, total) => {
        if (alive.current && !request.signal.aborted) setProgress({ done, total });
      }),
      loadPrintSender(organization.id, request.signal),
    ])
      .then(([deliveries, senderResult]) => {
        if (!alive.current || request.signal.aborted) return;
        if (deliveries.status === 'fulfilled') {
          setRows(deliveries.value);
          setPreparedAt(new Date().toISOString());
        } else if (deliveries.reason instanceof HandoverPreparationError)
          setIssues(deliveries.reason.issues);
        else
          setError(
            deliveries.reason instanceof Error
              ? deliveries.reason.message
              : 'The deliveries could not be prepared.',
          );
        if (senderResult.status === 'fulfilled') {
          setSavedSender(senderResult.value);
          setIncludeSender(!!senderResult.value);
        } else {
          setSavedSender(null);
          setIncludeSender(true);
          setSenderError(
            senderResult.reason instanceof Error
              ? senderResult.reason.message
              : 'The saved sender could not be loaded.',
          );
        }
      })
      .finally(() => {
        clearTimeout(timeout);
        if (operation.current === request) operation.current = null;
        if (alive.current && !request.signal.aborted) setLoading(false);
      });
    return () => {
      clearTimeout(timeout);
      request.abort();
    };
    // Parent freezes selection and remounts on organization/access/selection changes.
  }, [selection, organization?.id, profile?.id, access?.access_version, reload]);

  useEffect(() => {
    setPages(null);
    setLayoutError('');
    if (!rows?.length || !measuring.current) return;
    const request = new AbortController();
    const root = measuring.current;
    void waitForHandoverResources(root, request.signal)
      .then(() => {
        if (!alive.current || request.signal.aborted) return;
        setPages(measureHandoverPages(root, groups));
      })
      .catch((failure) => {
        if (alive.current && !request.signal.aborted)
          setLayoutError(
            failure instanceof Error ? failure.message : 'The A4 layout could not be prepared.',
          );
      });
    return () => request.abort();
  }, [rows, groups, includePhone, sender, preparedAt, timezone]);
  useEffect(() => {
    if (!pages || !preview.current) return;
    try {
      assertHandoverFits(preview.current);
    } catch (failure) {
      setLayoutError(failure instanceof Error ? failure.message : 'The preview does not fit A4.');
    }
  }, [pages]);
  useEffect(() => {
    const root = viewport.current;
    if (!root) return;
    const observer = new ResizeObserver(([entry]) =>
      setScale(Math.min(1, Math.max(0.1, (entry?.contentRect.width || 1) / ((190 * 96) / 25.4)))),
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, [pages, actualSize]);

  async function print() {
    if (!ready || !rows || operation.current || !organization?.id) return;
    const request = new AbortController();
    operation.current = request;
    setChecking(true);
    setError('');
    setNotice('');
    let handedToPrint = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const masked = includePhone ? await askPrivacy() : false;
      if (masked === null || request.signal.aborted || !alive.current) return;
      timeout = setTimeout(() => {
        request.abort();
        if (operation.current !== request || !alive.current) return;
        operation.current = null;
        setRows(null);
        setPages(null);
        setAcknowledged(false);
        setChecking(false);
        setError(
          'The freshness check timed out. Check your connection and prepare again before printing.',
        );
      }, 15000);
      const current = await loadHandoverSelection(handoverSelectionFromRows(rows), request.signal);
      if (JSON.stringify(current) !== JSON.stringify(rows))
        throw new Error('The deliveries changed. Prepare and review a new worksheet.');
      if (includeSender) {
        const latest = await loadPrintSender(organization.id, request.signal);
        if (
          !latest ||
          latest.version !== savedSender?.version ||
          JSON.stringify(latest.sender) !== JSON.stringify(savedSender.sender)
        )
          throw new Error('The saved sender changed. Prepare again to review the current address.');
      }
      if (!alive.current || request.signal.aborted || !preview.current) return;
      assertHandoverFits(preview.current);
      const html = customerPrintHtml(preview.current, masked);
      clearTimeout(timeout);
      setChecking(false);
      printHtmlContent(html, {
        documentTitle: 'KLOTHIQ courier handover',
        signal: request.signal,
        onBeforePrint: () => {
          if (alive.current) setPrinting(true);
        },
        onAfterPrint: () => {
          if (operation.current === request) operation.current = null;
          if (alive.current) {
            setPrinting(false);
            setNotice('Worksheet prepared. Your browser controls printing and Save as PDF.');
            requestAnimationFrame(() => printButton.current?.focus());
          }
        },
      });
      handedToPrint = true;
    } catch (failure) {
      if (alive.current && !request.signal.aborted) {
        setPrinting(false);
        setRows(null);
        setPages(null);
        setAcknowledged(false);
        if (failure instanceof HandoverPreparationError) setIssues(failure.issues);
        else
          setError(
            failure instanceof Error
              ? failure.message
              : 'The worksheet could not be checked. Prepare again.',
          );
      }
    } finally {
      clearTimeout(timeout);
      const ownsOperation = operation.current === request;
      if (!handedToPrint && ownsOperation) operation.current = null;
      if (alive.current && ownsOperation) {
        setChecking(false);
        if (!handedToPrint) requestAnimationFrame(() => printButton.current?.focus());
      }
    }
  }

  return (
    <>
      <WorkModalShell open wide title="Courier handover" busy={printing} onClose={onClose}>
        <header className="flex items-start justify-between gap-3 border-b border-border py-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <ClipboardList className="h-5 w-5 text-primary" aria-hidden="true" />
              Courier handover
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {selection.length} selected · {groups.length} courier{' '}
              {groups.length === 1 ? 'group' : 'groups'}
              {pages ? ` · ${pages.length} A4 ${pages.length === 1 ? 'page' : 'pages'}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:bg-muted"
            disabled={printing}
            aria-label="Close courier handover"
            onClick={onClose}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
          <p className="rounded-xl border border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
            Internal worksheet for saved Packed/Dispatched Courier records. Parcel counts, receiver
            details and signatures are completed by hand. Printing does not update shipment status.
          </p>
          {loading && (
            <div role="status" className="flex items-center gap-2 text-sm">
              <Loader2
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              Preparing deliveries {progress.done} of {progress.total}…
            </div>
          )}
          {!!issues.length && (
            <section
              role="alert"
              className="space-y-2 rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-sm"
            >
              <h3 className="font-semibold">No worksheet prepared</h3>
              <ul className="space-y-2">
                {issues.map((issue, i) => (
                  <li key={i}>
                    <strong>{issue.saleNumber}</strong>
                    <p className="text-xs leading-relaxed">{issue.message}</p>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                Your selection is kept. Close, correct these deliveries, refresh and reselect them.
              </p>
            </section>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          {!loading && (!rows || senderError) && (
            <button
              type="button"
              className={deliveryButton}
              disabled={busy}
              onClick={() => setReload((value) => value + 1)}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Prepare again
            </button>
          )}
          {!!rows?.length && (
            <>
              <fieldset className="space-y-3 rounded-xl border border-border p-3" disabled={busy}>
                <legend className="px-1 text-xs font-semibold">Worksheet options</legend>
                <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={includePhone}
                    onChange={(event) => {
                      setIncludePhone(event.target.checked);
                      setNotice('');
                    }}
                    className="h-4 w-4 accent-primary"
                  />
                  Include customer phone column
                </label>
                <p className="text-xs text-muted-foreground">
                  Phone numbers are omitted by default. If included, you choose masking before each
                  print.
                </p>
                {savedSender && (
                  <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={includeSender}
                      onChange={(event) => {
                        setIncludeSender(event.target.checked);
                        setNotice('');
                      }}
                      className="h-4 w-4 accent-primary"
                    />
                    Include saved sender details
                  </label>
                )}
                {!savedSender && !senderError && (
                  <p className="text-xs text-muted-foreground">
                    No saved sender address. The worksheet uses the brand name and logo only.
                  </p>
                )}
                <Link
                  to="/print-templates"
                  className="inline-flex min-h-11 items-center text-xs text-primary"
                >
                  Manage saved sender in Print templates
                </Link>
              </fieldset>
              {senderError && (
                <section className="space-y-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                  <p role="alert" className="text-xs text-amber-800 dark:text-amber-200">
                    {senderError}
                  </p>
                  <button
                    type="button"
                    disabled={busy}
                    className={deliveryButton}
                    onClick={() => {
                      setBrandOnly(true);
                      setIncludeSender(false);
                      setNotice(
                        'Brand-only output selected. The saved sender address will not be printed.',
                      );
                    }}
                  >
                    Use brand-only output
                  </button>
                </section>
              )}
              {!!duplicates.length && (
                <section className="space-y-3 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Repeated tracking references
                  </h3>
                  <ul className="space-y-2 text-xs">
                    {duplicates.map((duplicate, i) => (
                      <li key={i} className="break-words">
                        <strong>{duplicate.courier}</strong>
                        <p className="break-all font-mono">{duplicate.tracking}</p>
                        <p>{duplicate.saleNumbers.join(', ')}</p>
                      </li>
                    ))}
                  </ul>
                  <label className="flex min-h-11 cursor-pointer items-start gap-2 text-xs leading-relaxed">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-primary"
                      checked={acknowledged}
                      disabled={busy}
                      onChange={(event) => setAcknowledged(event.target.checked)}
                    />
                    I reviewed these repeated references. Keep all listed sales; physical parcel
                    counts will be confirmed by hand.
                  </label>
                </section>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-medium">
                  {pages
                    ? `${pages.length} prepared A4 ${pages.length === 1 ? 'sheet' : 'sheets'}`
                    : 'Measuring readable A4 pages…'}
                </p>
                <button
                  type="button"
                  className={deliveryButton}
                  disabled={busy}
                  onClick={() => setActualSize((value) => !value)}
                >
                  {actualSize ? 'Fit width' : 'Actual size'}
                </button>
              </div>
              {layoutError && (
                <p role="alert" className="text-sm text-destructive">
                  {layoutError}
                </p>
              )}
              <div
                ref={viewport}
                className="min-w-0 overflow-auto rounded-xl border border-border bg-muted/30"
                aria-label="A4 handover preview"
              >
                {pages && (
                  <div
                    style={{
                      width: actualSize ? '190mm' : '100%',
                      height: actualSize
                        ? undefined
                        : `${pages.length * ((276 * 96) / 25.4 + 16) * scale}px`,
                    }}
                  >
                    <div
                      style={{
                        width: '190mm',
                        transform: actualSize ? undefined : `scale(${scale})`,
                        transformOrigin: 'top left',
                      }}
                    >
                      <div ref={preview}>
                        <CourierHandoverSheets
                          pages={pages}
                          preparedAt={preparedAt}
                          timezone={timezone}
                          includePhone={includePhone}
                          sender={sender}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {notice && (
            <p role="status" className="text-xs text-muted-foreground">
              {notice}
            </p>
          )}
        </div>
        <footer className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
          <button type="button" className={deliveryButton} disabled={printing} onClick={onClose}>
            {loading || checking ? 'Cancel' : 'Close'}
          </button>
          <button
            ref={printButton}
            type="button"
            className={cn(deliveryButton, 'bg-primary text-primary-foreground hover:bg-primary/90')}
            disabled={!ready || busy}
            onClick={() => void print()}
          >
            {checking || printing ? (
              <Loader2
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            ) : (
              <Printer className="h-4 w-4" aria-hidden="true" />
            )}
            {checking ? 'Checking saved details…' : printing ? 'Printing…' : 'Print / Save as PDF'}
          </button>
        </footer>
        {!!rows?.length && (
          <div
            ref={measuring}
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: '-10000px',
              top: 0,
              width: '190mm',
              visibility: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <CourierHandoverSheets
              pages={measurementPages}
              preparedAt={preparedAt}
              timezone={timezone}
              includePhone={includePhone}
              sender={sender}
              measurement
            />
          </div>
        )}
      </WorkModalShell>
      {privacyDialog}
    </>
  );
}
